import { CONSTANTS } from '../../_data/lookups.js';
import { bindInternalInputPotential, } from '../mutableWorkflowOverlay.js';
import { extractToolStepsFromStep } from '../stepExpansion.js';
import { mutableStepToStep, projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta, } from '../workflowCanonicalization.js';
function toMutableToolStepRoleAddress(toolStep, roleName) {
    return {
        stepKey: toolStep.stepKey,
        roleName,
    };
}
export function getSingleNonErrorOutputToolStepRoleAddress(toolStep, nodeName) {
    const outputs = toolStep.roleBindingSpec.outputBindings;
    const nonErrorRoleNames = outputs.filter((roleName) => roleName !== CONSTANTS.Cosmos.RoleNames.ErrorOutput);
    if (nonErrorRoleNames.length !== 1) {
        throw new Error(`[${nodeName}] ToolStep must have exactly one non-error output role binding; got ${nonErrorRoleNames.length} (toolStep ${toolStep.stepKey})`);
    }
    if (!nonErrorRoleNames[0]) {
        throw new Error(`[${nodeName}] Non-error output role name is undefined or empty for toolStep ${toolStep.stepKey}`);
    }
    return toMutableToolStepRoleAddress(toolStep, nonErrorRoleNames[0]);
}
export function buildToolStepByKeyMapForThread(threadSteps, cloneToolSteps = []) {
    const map = new Map();
    const addFromStep = (step) => {
        switch (step.stepKind) {
            case CONSTANTS.Enums.StepKind.tool:
                map.set(step.stepKey, step);
                break;
            case CONSTANTS.Enums.StepKind.branch:
                for (const branchCase of step.cases) {
                    map.set(branchCase.what.stepKey, branchCase.what);
                    map.set(branchCase.when.stepKey, branchCase.when);
                }
                break;
            case CONSTANTS.Enums.StepKind.while:
            case CONSTANTS.Enums.StepKind.for:
                map.set(step.case.what.stepKey, step.case.what);
                map.set(step.case.when.stepKey, step.case.when);
                break;
        }
    };
    for (const step of threadSteps) {
        addFromStep(step);
    }
    for (const toolStep of cloneToolSteps) {
        map.set(toolStep.stepKey, toolStep);
    }
    return map;
}
export function mergeWorkflowStateDelta(base, next) {
    return {
        ...base,
        ...next,
    };
}
export function projectChangedToolStepsToWorkflowStateDelta(updatedWorkflow, changedToolStepKeys, nextWorkflowState) {
    let workflowStateDelta = {};
    for (const changedToolStepKey of changedToolStepKeys) {
        const workflowStateEntries = nextWorkflowState[changedToolStepKey];
        if (!workflowStateEntries) {
            continue;
        }
        workflowStateDelta = mergeWorkflowStateDelta(workflowStateDelta, projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta(updatedWorkflow, changedToolStepKey, workflowStateEntries));
    }
    return workflowStateDelta;
}
export function insertStepsIntoThread(threadSteps, currentStepCounter, insertedSteps) {
    return [
        ...threadSteps.slice(0, currentStepCounter + 1),
        ...insertedSteps,
        ...threadSteps.slice(currentStepCounter + 1),
    ];
}
export function projectMutableStepsToTraceSteps(insertedSteps) {
    return insertedSteps.map(mutableStepToStep);
}
export function withThreadStepsAtIndex(stepsByThreadIndex, threadIndex, nextThreadSteps) {
    const nextStepsByThreadIndex = [...stepsByThreadIndex];
    nextStepsByThreadIndex[threadIndex] = nextThreadSteps;
    return nextStepsByThreadIndex;
}
export function withUpdatedWorkflowSteps(mutableWorkflow, stepsByThreadIndex) {
    return {
        ...mutableWorkflow,
        stepsByThreadIndex,
    };
}
export function withUpdatedWorkflowStepsAndState(mutableWorkflow, stepsByThreadIndex, workflowState) {
    return {
        ...mutableWorkflow,
        workflowState,
        stepsByThreadIndex,
    };
}
export function toolStepRoleAddressEquals(a, b) {
    return a.stepKey === b.stepKey && a.roleName === b.roleName;
}
export function rebindDownstreamInternalInputPotentials(nextWorkflowState, nextStepCounter, threadSteps, bindingContext, decideTargetAddress) {
    const changedToolStepKeys = new Set();
    for (let idx = nextStepCounter; idx < threadSteps.length; idx++) {
        const step = threadSteps[idx];
        if (!step) {
            continue;
        }
        const toolSteps = extractToolStepsFromStep(step);
        for (const toolStep of toolSteps) {
            const inputRoleNames = toolStep.roleBindingSpec?.inputBindings ?? [];
            for (const inputRoleName of inputRoleNames) {
                const workflowStateEntry = nextWorkflowState?.[toolStep.stepKey]?.[inputRoleName];
                if (!workflowStateEntry) {
                    continue;
                }
                if (workflowStateEntry.workflowStateEntryKind !== CONSTANTS.Enums.WorkflowStateEntryKind.internalInputPotential) {
                    continue;
                }
                const targetAddress = decideTargetAddress(workflowStateEntry.toolStepRoleAddress, toolStep.stepKey, inputRoleName);
                if (!targetAddress) {
                    continue;
                }
                nextWorkflowState = bindInternalInputPotential(nextWorkflowState, {
                    stepKey: toolStep.stepKey,
                    roleName: inputRoleName,
                }, targetAddress, bindingContext);
                changedToolStepKeys.add(toolStep.stepKey);
            }
        }
    }
    return {
        nextWorkflowState,
        changedToolStepKeys,
    };
}
